import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MatchCard } from "./PropertyRequestMatches";
import { formatDate } from "./labels";
import { propertyRequestApi } from "../../Services/propertyRequestService";

// Proposals received for one request (owner only). Opening them marks new ones as seen on the server.
export default function ReceivedProposals({ requestId, onLoaded }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, error: "", items: [] });

  useEffect(() => {
    let current = true;
    propertyRequestApi.proposals(requestId)
      .then((items) => { if (current) { setState({ loading: false, error: "", items: items || [] }); onLoaded?.(items || []); } })
      .catch((error) => { if (current) setState({ loading: false, error: error.message, items: [] }); });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const meta = (p) => [p.publisherName ? t("propertyRequest.received.from", { name: p.publisherName }) : t("propertyRequest.received.fromUnknown"),
    t("propertyRequest.received.date", { date: formatDate(p.createdAt, i18n.language) })].join(" · ");
  const newBadge = (p) => (p.status === "New" ? <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">{t("propertyRequest.received.newBadge")}</span> : null);
  const details = (p) => (
    <div className="mx-3 mb-3 rounded-2xl border border-slate-100 px-4 py-3 text-sm sm:mx-4">
      <p className="font-semibold text-slate-800">{meta(p)}</p>
      {p.message && (<><p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-500">{t("propertyRequest.received.message")}</p>
        <p className="whitespace-pre-line break-words text-slate-800">{p.message}</p></>)}
    </div>
  );

  return (
    <section className="space-y-3 border-t border-slate-100 bg-slate-50 p-3 sm:p-4" aria-label={t("propertyRequest.received.title")} data-received={requestId}>
      <h4 className="text-base font-bold text-slate-900">{t("propertyRequest.received.title")}</h4>
      {state.loading && <p role="status" className="text-sm text-slate-600">{t("propertyRequest.received.loading")}</p>}
      {state.error && <p role="alert" className="text-sm font-semibold text-red-700">{state.error}</p>}
      {!state.loading && !state.error && state.items.length === 0 && <p className="text-sm text-slate-600">{t("propertyRequest.received.none")}</p>}
      {state.items.map((p) => (p.available ? (
        <div key={p.id} data-proposal={p.id}>
          <MatchCard match={p} aside={newBadge(p)}>{details(p)}</MatchCard>
        </div>
      ) : (
        <article key={p.id} data-proposal={p.id} className="rounded-3xl border border-slate-200 bg-white p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">{newBadge(p)}<p className="font-semibold text-slate-700">{t("propertyRequest.received.unavailable")}</p></div>
          {details(p)}
        </article>
      )))}
    </section>
  );
}
